import { useEffect, useState } from "react";
import md5 from 'js-md5';
import './ProfilePicture.scss';

const AVATAR_TONE_CLASS_NAMES = [
    'profile-pic--tone-gold',
    'profile-pic--tone-teal',
    'profile-pic--tone-blue',
    'profile-pic--tone-red',
    'profile-pic--tone-slate',
];

const normalizeIdentityPart = (value) => String(value || '').trim();

const getEmailLocalPart = (email) => normalizeIdentityPart(email).split('@')[0] || '';

const buildInitialsFromValue = (value) => {
    const normalizedValue = normalizeIdentityPart(value)
        .replace(/[^a-zA-Z0-9._+\-\s]/g, ' ')
        .trim();

    if (!normalizedValue) {
        return '';
    }

    const tokens = normalizedValue.split(/[._+\-\s]+/).filter(Boolean);

    if (tokens.length >= 2) {
        return `${tokens[0][0]}${tokens[1][0]}`.toUpperCase();
    }

    const compactValue = normalizedValue.replace(/[^a-zA-Z0-9]/g, '');
    return compactValue.slice(0, 2).toUpperCase();
};

export const getAvatarIdentityValue = (user) => {
    const emailLocalPart = getEmailLocalPart(user?.email);

    return emailLocalPart ||
        normalizeIdentityPart(user?.username) ||
        normalizeIdentityPart(`${user?.first_name || ''} ${user?.last_name || ''}`) ||
        'PC';
};

export const getAvatarInitials = (user) => buildInitialsFromValue(getAvatarIdentityValue(user)) || 'PC';

export const getAvatarToneClassName = (user) => {
    const identityValue = getAvatarIdentityValue(user);
    const hashValue = identityValue.split('').reduce((total, character) => total + character.charCodeAt(0), 0);

    return AVATAR_TONE_CLASS_NAMES[hashValue % AVATAR_TONE_CLASS_NAMES.length];
};

const ProfilePicture = ({ user, progress = false, notificationBadgeText = '', showPremiumStar = false }) => {
    const emailHash = md5((user?.email || "").trim().toLowerCase());
    const imageUrl = user?.email ? `https://www.gravatar.com/avatar/${emailHash}?s=200&d=404` : '';
    const initials = getAvatarInitials(user);
    const toneClassName = getAvatarToneClassName(user);
    const [imageLoadFailed, setImageLoadFailed] = useState(!imageUrl);
  
    const radius = 90; // Sized to avoid clipping the ring.
    const strokeWidth = 16;
    const adjustedRadius = radius - strokeWidth / 2;
    const circumference = 2 * Math.PI * adjustedRadius;
  
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference * ((100 - (progress===0?15:progress)) / 100);
  
    const [progressValue, setProgressValue] = useState(circumference);
  
    useEffect(() => {
      setTimeout(() => setProgressValue(strokeDashoffset), 100);
    }, [progress]);

    useEffect(() => {
      setImageLoadFailed(!imageUrl);
    }, [imageUrl]);
  
    return (
      <div className="profile-container">
        {!imageLoadFailed &&
          <img
            src={imageUrl}
            alt="Profile"
            className="profile-pic"
            onError={() => setImageLoadFailed(true)}
          />
        }
        {imageLoadFailed &&
          <div className={`profile-pic profile-pic-fallback ${toneClassName}`} aria-label="Profile">
            {initials}
          </div>
        }
        {notificationBadgeText &&
          <span className="profile-notification-badge">{notificationBadgeText}</span>
        }
        {showPremiumStar &&
          <span aria-label="PRO asset access" className="profile-premium-badge" title="PRO asset access">
            <span aria-hidden="true" className="glyphicon glyphicon-star" />
          </span>
        }
        {progress !== false && (
          <svg className="progress-bar" viewBox="0 0 200 200" style={{ transform: "rotate(-90deg)" }}>
            <circle
              r={adjustedRadius}
              cx="100"
              cy="100"
              fill="transparent"
              stroke="#e0e0e0"
              strokeWidth={strokeWidth}
            ></circle>
            <circle
              r={adjustedRadius}
              cx="100"
              cy="100"
              stroke="#f36c17"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              fill="transparent"
              className="progress-ring"
              style={{ strokeDasharray, strokeDashoffset: progressValue }}
            ></circle>
          </svg>
        )}
      </div>
    );
};

export default ProfilePicture;
